
/**
 * @license 
 * Copyright Google Inc. All Rights Reserved.
 * 
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
export class Tree<T> {
/**
 * \@internal
 */
_root: TreeNode<T>;
/**
 * @param {?} root
 */
constructor(root: TreeNode<T>) { this._root = root; }
/**
 * @return {?}
 */
get root(): T { return this._root.value; }
/**
 * \@internal
 * @param {?} t
 * @return {?}
 */
parent(t: T): T|null {
    const /** @type {?} */ p = this.pathFromRoot(t);
    return p.length > 1 ? p[p.length - 2] : null;
  }
/**
 * \@internal
 * @param {?} t
 * @return {?}
 */
children(t: T): T[] {
    const /** @type {?} */ n = findNode(t, this._root);
    return n ? n.children.map(t => t.value) : [];
  }
/**
 * \@internal
 * @param {?} t
 * @return {?}
 */
firstChild(t: T): T|null {
    const /** @type {?} */ n = findNode(t, this._root);
    return n && n.children.length > 0 ? n.children[0].value : null;
  }
/**
 * \@internal
 * @param {?} t
 * @return {?}
 */
siblings(t: T): T[] {
    const /** @type {?} */ p = findPath(t, this._root);
    if (p.length < 2) return [];

    const /** @type {?} */ c = p[p.length - 2].children.map(c => c.value);
    return c.filter(cc => cc !== t);
  }
/**
 * \@internal
 * @param {?} t
 * @return {?}
 */
pathFromRoot(t: T): T[] { return findPath(t, this._root).map(s => s.value); }
}

function Tree_tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
Tree.prototype._root;
}

/**
 * @template T
 * @param {?} value
 * @param {?} node
 * @return {?}
 */
function findNode<T>(value: T, node: TreeNode<T>): TreeNode<T>|null {
  if (value === node.value) return node;

  for (const /** @type {?} */ child of node.children) {
    const /** @type {?} */ node = findNode(value, child);
    if (node) return node;
  }

  return null;
}
/**
 * @template T
 * @param {?} value
 * @param {?} node
 * @return {?}
 */
function findPath<T>(value: T, node: TreeNode<T>): TreeNode<T>[] {
  if (value === node.value) return [node];

  for (const /** @type {?} */ child of node.children) {
    const /** @type {?} */ path = findPath(value, child);
    if (path.length) {
      path.unshift(node);
      return path;
    }
  }

  return [];
}
export class TreeNode<T> {
/**
 * @param {?} value
 * @param {?} children
 */
constructor(public value: T,
public children: TreeNode<T>[]) {}
/**
 * @return {?}
 */
toString(): string { return `TreeNode(${this.value})`; }
}

function TreeNode_tsickle_Closure_declarations() {
/** @type {?} */
TreeNode.prototype.value;
/** @type {?} */
TreeNode.prototype.children;
}



// WEBPACK FOOTER //
// ../../../../packages/router/src/utils/tree.ts